import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { getRoleConfig } from '../config/auth';
import { 
  UserIcon, 
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  CogIcon,
  ClockIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const UserProfile = () => {
  const { userRole, logout, isFounder, isInvestor, isClient, getSessionTimeRemaining, sessionInfo } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');

  const roleConfig = getRoleConfig(userRole);

  // Update session timer
  useEffect(() => {
    if (!sessionInfo) return;

    const updateTimer = () => {
      const remaining = getSessionTimeRemaining();
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      
      if (remaining > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else {
        setTimeRemaining('Expired');
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [sessionInfo, getSessionTimeRemaining]);

  const handleLogout = () => {
    logout('manual');
    setIsDropdownOpen(false);
  };

  const handleQuickAction = (action) => {
    // Track action for analytics
    if (window.posthog) {
      window.posthog.capture('profile_action', {
        action: action,
        role: userRole
      });
    }

    // Handle role-specific actions
    switch (action) {
      case 'admin':
        // TODO: Navigate to admin panel
        const isDev = (() => {
          try {
            return process?.env?.NODE_ENV === 'development';
          } catch {
            return false;
          }
        })();
        
        if (isDev) {
          console.log('Navigating to admin panel...');
        }
        break;
      case 'dashboard':
        // TODO: Navigate to investment dashboard
        if (isDev) {
          console.log('Navigating to investment dashboard...');
        }
        break;
      case 'consultation':
        // TODO: Schedule consultation
        if (isDev) {
          console.log('Scheduling consultation...');
        }
        break;
      default:
        if (isDev) {
          console.log('Unknown action:', action);
        }
    }
    
    setIsDropdownOpen(false);
  };

  if (!roleConfig) return null;

  return (
    <div className="relative">
      <motion.button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="flex items-center space-x-3 bg-white/10 hover:bg-white/15 rounded-xl px-4 py-2 transition-all duration-300"
      >
        <div className={`w-8 h-8 bg-gradient-to-br ${roleConfig.color} rounded-full flex items-center justify-center shadow-lg`}>
          <UserIcon className="h-4 w-4 text-white" />
        </div>
        <div className="hidden sm:block text-left">
          <div className="text-cream-100 text-sm font-medium">{roleConfig.label}</div>
          <div className="text-cream-300 text-xs">{roleConfig.description}</div>
        </div>
        <ChevronDownIcon 
          className={`h-4 w-4 text-cream-300 transition-transform duration-200 ${
            isDropdownOpen ? 'rotate-180' : ''
          }`} 
        />
      </motion.button>

      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-full mt-2 w-64 glass-card rounded-xl shadow-2xl z-50 border border-white/10"
          >
            <div className="p-4">
              {/* User Info Header */}
              <div className="flex items-center space-x-3 mb-4 pb-4 border-b border-white/10">
                <div className={`w-10 h-10 bg-gradient-to-br ${roleConfig.color} rounded-full flex items-center justify-center shadow-lg`}>
                  <UserIcon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="text-cream-100 font-medium">{roleConfig.label}</div>
                  <div className="text-cream-300 text-sm">{roleConfig.description}</div>
                </div>
              </div>

              {/* Session Info */}
              {sessionInfo && (
                <div className="mb-4 pb-4 border-b border-white/10">
                  <div className="flex items-center space-x-2 text-cream-200 text-xs mb-2">
                    <ClockIcon className="h-3 w-3" />
                    <span>Session: {timeRemaining}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-cream-300 text-xs">
                    <ShieldCheckIcon className="h-3 w-3" />
                    <span>ID: {sessionInfo.sessionId.slice(0, 8)}...</span>
                  </div>
                </div>
              )}

              {/* Role-based Quick Actions */}
              <div className="space-y-2 mb-4">
                {isFounder() && (
                  <>
                    <button 
                      onClick={() => handleQuickAction('admin')}
                      className="w-full flex items-center space-x-3 text-cream-200 hover:text-cream-100 p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
                    >
                      <CogIcon className="h-4 w-4" />
                      <span className="text-sm">Admin Dashboard</span>
                    </button>
                    <button 
                      onClick={() => handleQuickAction('analytics')}
                      className="w-full flex items-center space-x-3 text-cream-200 hover:text-cream-100 p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
                    >
                      <ShieldCheckIcon className="h-4 w-4" />
                      <span className="text-sm">System Analytics</span>
                    </button>
                  </>
                )}
                
                {isInvestor() && (
                  <>
                    <button 
                      onClick={() => handleQuickAction('dashboard')}
                      className="w-full flex items-center space-x-3 text-cream-200 hover:text-cream-100 p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
                    >
                      <CogIcon className="h-4 w-4" />
                      <span className="text-sm">Investment Dashboard</span>
                    </button>
                    <button 
                      onClick={() => handleQuickAction('reports')}
                      className="w-full flex items-center space-x-3 text-cream-200 hover:text-cream-100 p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
                    >
                      <UserIcon className="h-4 w-4" />
                      <span className="text-sm">Performance Reports</span>
                    </button>
                  </>
                )}
                
                {isClient() && (
                  <>
                    <button 
                      onClick={() => handleQuickAction('consultation')}
                      className="w-full flex items-center space-x-3 text-cream-200 hover:text-cream-100 p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
                    >
                      <UserIcon className="h-4 w-4" />
                      <span className="text-sm">Schedule Consultation</span>
                    </button>
                    <button 
                      onClick={() => handleQuickAction('support')}
                      className="w-full flex items-center space-x-3 text-cream-200 hover:text-cream-100 p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
                    >
                      <CogIcon className="h-4 w-4" />
                      <span className="text-sm">Support Portal</span>
                    </button>
                  </>
                )}
              </div>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-3 text-red-400 hover:text-red-300 p-2 rounded-lg hover:bg-red-400/10 transition-all duration-200 border-t border-white/10 pt-4"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
                <span className="text-sm font-medium">Sign Out</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click outside to close */}
      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsDropdownOpen(false)}
        />
      )}
    </div>
  );
};

export default UserProfile;